/**
 * Componente para mostrar un stream WebRTC de MediaMTX
 * Se conecta a MediaMTX usando WebRTC (WHEP) para baja latencia
 */

import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { getToken } from '../../services/auth';

interface VideoStreamViewerProps {
  cameraId: number;
  cameraName: string;
  onError?: (error: string) => void;
  rotation?: number; // Rotación en grados (0, 90, 180, 270)
  hasZoom?: boolean; // Indica si hay zoom activo - para controlar pointer-events
  quality?: 'sd' | 'hd'; // 'sd' = Sub Stream 1 (default), 'hd' = Main Stream bajo demanda
  audioEnabled?: boolean; // Si true, se reproduce el audio de la cámara (default: muteado)
  onAspectRatio?: (ratio: number) => void; // Relación de aspecto real del stream (ancho/alto)
  /** Expone el MediaStream activo para reutilizarlo (p. ej. fullscreen) sin nueva sesión WHEP. */
  onStreamReady?: (stream: MediaStream | null) => void;
  /** Reporta el consumo entrante medido (kbps) o null al desconectar / limpiar. */
  onBandwidth?: (cameraId: number, kbps: number | null) => void;
  /** Retrasa el POST WHEP (p. ej. timbre Grandstream cuando arrancan todas a la vez). */
  connectDelayMs?: number;
  /** Si es false, no muestra el indicador de kbps. Default true. */
  showBandwidth?: boolean;
  /** Alineación del cuadro dentro del recuadro (object-position). */
  objectPosition?: string;
  /** Timbre: conectar aunque el documento nazca hidden (apertura desde notificación). */
  connectWhileHidden?: boolean;
}

/** Formatea el consumo medido para el overlay (kbps; Mbps si supera 1000). */
export function formatBandwidthKbps(kbps: number): string {
  if (kbps >= 1000) {
    return `${(kbps / 1000).toFixed(1)} Mbps`;
  }
  return `${Math.round(kbps)} kbps`;
}

/** Suma bytes recibidos (video + audio) desde reportes inbound-rtp de getStats. */
function sumInboundBytes(stats: RTCStatsReport): number {
  let total = 0;
  stats.forEach((report) => {
    if (report.type === 'inbound-rtp') {
      const bytes = (report as RTCInboundRtpStreamStats).bytesReceived;
      if (typeof bytes === 'number') {
        total += bytes;
      }
    }
  });
  return total;
}

/**
 * Resuelve la URL de recurso WHEP del header Location del POST.
 * MediaMTX (v1.17) devuelve paths como "/camera_1/whep/{secret}" sin el prefijo
 * "/webrtc/" que usa nginx; hay que añadirlo para que el DELETE llegue al proxy.
 */
function resolveWhepResourceUrl(locationHeader: string | null, whepPostPath: string): string | null {
  if (!locationHeader?.trim()) {
    return null;
  }
  const location = locationHeader.trim();
  try {
    if (location.startsWith('/')) {
      const path = location.startsWith('/webrtc/')
        ? location
        : `/webrtc${location}`;
      return new URL(path, window.location.origin).href;
    }
    const base = new URL(whepPostPath, window.location.origin).toString();
    return new URL(location, base).href;
  } catch {
    return null;
  }
}

/** Termina la sesión WHEP en MediaMTX (HTTP DELETE). */
async function tearDownWhepSession(resourceUrl: string | null, authToken: string | null): Promise<void> {
  if (!resourceUrl) {
    return;
  }
  try {
    await fetch(resourceUrl, {
      method: 'DELETE',
      headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      keepalive: true,
    });
  } catch {
    /* no-op: la conexión ya se está cerrando */
  }
}

function closePeerConnection(pc: RTCPeerConnection | null): void {
  if (!pc) {
    return;
  }
  try {
    pc.getReceivers().forEach((receiver) => {
      if (receiver.track) {
        receiver.track.stop();
      }
    });
  } catch {
    /* no-op */
  }
  try {
    pc.close();
  } catch {
    /* no-op */
  }
}

// Función para detectar iOS
const isIOS = (): boolean => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
};

// Función para detectar Safari (macOS o iOS) o navegadores basados en WebKit en iOS
// Safari en macOS también tiene el mismo problema con WebRTC
const isSafariOrWebKit = (): boolean => {
  const userAgent = navigator.userAgent.toLowerCase();
  const isIOSDevice = isIOS();
  
  // Safari en macOS o iOS
  const isSafari = userAgent.includes('safari') && 
                   !userAgent.includes('chrome') && 
                   !userAgent.includes('chromium');
  
  // Chrome en iOS (usa WebKit, tiene el mismo problema)
  const isChromeIOS = isIOSDevice && (userAgent.includes('crios') || userAgent.includes('chrome'));
  
  return isSafari || isChromeIOS;
};

// Función para normalizar oferta SDP para Safari/WebKit (asegurar que tenga atributos ICE)
const normalizeOfferSDPForSafari = (sdp: string): string => {
  if (!isSafariOrWebKit()) {
    return sdp;
  }

  const lines = sdp.split('\r\n');
  let hasIceUfrag = false;
  let hasIcePwd = false;
  let iceUfrag = '';
  let icePwd = '';
  let mLineIndex = -1;
  let firstMediaLineIndex = -1;

  // Buscar atributos ICE existentes y primera línea m=
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.startsWith('m=')) {
      if (firstMediaLineIndex === -1) {
        firstMediaLineIndex = i;
      }
      if (mLineIndex === -1) {
        mLineIndex = i;
      }
    } else if (line.startsWith('a=ice-ufrag:')) {
      hasIceUfrag = true;
      iceUfrag = line.substring('a=ice-ufrag:'.length);
    } else if (line.startsWith('a=ice-pwd:')) {
      hasIcePwd = true;
      icePwd = line.substring('a=ice-pwd:'.length);
    }
  }

  // Si faltan atributos ICE, generarlos
  if (!hasIceUfrag || !hasIcePwd) {
    // Generar valores ICE si no existen
    if (!iceUfrag) {
      iceUfrag = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 10);
    }
    if (!icePwd) {
      icePwd = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 10);
    }

    // Buscar dónde insertar los atributos ICE
    // Preferiblemente después de la primera línea m=, o después de atributos a= existentes
    let insertIndex = -1;
    
    if (firstMediaLineIndex >= 0) {
      // Buscar el primer atributo 'a=' después de la línea m=
      for (let i = firstMediaLineIndex + 1; i < lines.length; i++) {
        if (lines[i].startsWith('a=')) {
          insertIndex = i;
          break;
        }
        // Si encontramos otra línea m= o línea vacía, insertar antes
        if (lines[i].startsWith('m=') || lines[i] === '') {
          insertIndex = i;
          break;
        }
      }
      
      // Si no encontramos atributo a=, insertar después de m=
      if (insertIndex === -1) {
        insertIndex = firstMediaLineIndex + 1;
      }
    } else {
      // Si no hay línea m=, buscar el final de los atributos globales
      for (let i = 0; i < lines.length; i++) {
        if (lines[i].startsWith('a=')) {
          insertIndex = i + 1;
        }
      }
      if (insertIndex === -1) {
        insertIndex = lines.length;
      }
    }

    // Insertar atributos ICE
    if (insertIndex >= 0) {
      if (!hasIceUfrag) {
        lines.splice(insertIndex, 0, `a=ice-ufrag:${iceUfrag}`);
        insertIndex++; // Ajustar índice después de insertar
      }
      if (!hasIcePwd) {
        lines.splice(insertIndex, 0, `a=ice-pwd:${icePwd}`);
      }
    } else {
      // Fallback: agregar al final
      if (!hasIceUfrag) {
        lines.push(`a=ice-ufrag:${iceUfrag}`);
      }
      if (!hasIcePwd) {
        lines.push(`a=ice-pwd:${icePwd}`);
      }
    }
  }

  const normalizedSdp = lines.join('\r\n');
  console.log('[VideoStreamViewer] SDP normalizado:', {
    originalLength: sdp.length,
    normalizedLength: normalizedSdp.length,
    hasIceUfrag: normalizedSdp.includes('a=ice-ufrag:'),
    hasIcePwd: normalizedSdp.includes('a=ice-pwd:'),
  });
  
  return normalizedSdp;
};

// Función para validar y normalizar SDP answer para Safari/WebKit
const normalizeSDPForSafari = (sdp: string): string => {
  if (!isSafariOrWebKit()) {
    return sdp;
  }

  // iOS requiere que el SDP tenga ice-ufrag e ice-pwd en cada sección de media
  // Si falta, intentamos extraerlos de la sección global y agregarlos a cada sección de media
  const lines = sdp.split('\r\n');
  let globalIceUfrag = '';
  let globalIcePwd = '';
  const mediaSections: Array<{ mLineIndex: number; hasIceUfrag: boolean; hasIcePwd: boolean }> = [];
  let currentMediaIndex = -1;

  // Primera pasada: encontrar atributos ICE globales y secciones de media
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    if (line.startsWith('m=')) {
      currentMediaIndex++;
      mediaSections.push({ mLineIndex: i, hasIceUfrag: false, hasIcePwd: false });
    } else if (line.startsWith('a=ice-ufrag:')) {
      const iceUfrag = line.substring('a=ice-ufrag:'.length);
      if (currentMediaIndex < 0) {
        // Atributo ICE global (antes de cualquier sección m=)
        globalIceUfrag = iceUfrag;
      } else if (currentMediaIndex >= 0 && currentMediaIndex < mediaSections.length) {
        // Atributo ICE en sección de media
        mediaSections[currentMediaIndex].hasIceUfrag = true;
        // Si no tenemos global, usar este como referencia
        if (!globalIceUfrag) {
          globalIceUfrag = iceUfrag;
        }
      }
    } else if (line.startsWith('a=ice-pwd:')) {
      const icePwd = line.substring('a=ice-pwd:'.length);
      if (currentMediaIndex < 0) {
        // Atributo ICE global (antes de cualquier sección m=)
        globalIcePwd = icePwd;
      } else if (currentMediaIndex >= 0 && currentMediaIndex < mediaSections.length) {
        // Atributo ICE en sección de media
        mediaSections[currentMediaIndex].hasIcePwd = true;
        // Si no tenemos global, usar este como referencia
        if (!globalIcePwd) {
          globalIcePwd = icePwd;
        }
      }
    }
  }

  // Si no hay atributos ICE en absoluto, retornar SDP original (el fallback lo manejará)
  if (!globalIceUfrag || !globalIcePwd) {
    return sdp;
  }

  // Segunda pasada: agregar atributos ICE faltantes a secciones de media
  const result: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    if (line.startsWith('m=')) {
      result.push(line);
      // Buscar la sección correspondiente
      const section = mediaSections.find(s => s.mLineIndex === i);
      if (section) {
        // Si esta sección no tiene atributos ICE, los agregamos después de la línea m=
        if (!section.hasIceUfrag || !section.hasIcePwd) {
          // Buscar dónde insertar (después de m= y antes de otros atributos)
          let insertIndex = i + 1;
          // Avanzar hasta encontrar el primer atributo 'a=' o línea vacía
          while (insertIndex < lines.length && 
                 !lines[insertIndex].startsWith('a=') && 
                 lines[insertIndex] !== '' &&
                 !lines[insertIndex].startsWith('m=')) {
            insertIndex++;
          }
          
          // Insertar atributos ICE si faltan
          if (!section.hasIceUfrag) {
            result.push(`a=ice-ufrag:${globalIceUfrag}`);
          }
          if (!section.hasIcePwd) {
            result.push(`a=ice-pwd:${globalIcePwd}`);
          }
        }
      }
    } else {
      result.push(line);
    }
  }

  return result.join('\r\n');
};

const VideoStreamViewer: React.FC<VideoStreamViewerProps> = ({
  cameraId,
  cameraName,
  onError,
  rotation = 0,
  hasZoom = false,
  quality = 'sd',
  audioEnabled = false,
  onAspectRatio,
  onStreamReady,
  onBandwidth,
  connectDelayMs = 0,
  showBandwidth = true,
  objectPosition = 'center',
  connectWhileHidden = false,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const disconnectedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const emptyFrameRetriesRef = useRef(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Variante A: contador que re-dispara el efecto de streaming cuando cambia la
  // visibilidad de la app (minimizar / cambiar de pestaña o app / pantalla apagada).
  // Al ocultarse, la limpieza del efecto cierra la conexión (corta el ancho de
  // banda); al volver a estar visible, se vuelve a conectar.
  const [reconnectNonce, setReconnectNonce] = useState(0);
  const [bandwidthKbps, setBandwidthKbps] = useState<number | null>(null);
  const [bandwidthPending, setBandwidthPending] = useState(false);
  const statsPrevRef = useRef<{ bytes: number; time: number } | null>(null);

  // Reportar la relación de aspecto real del stream (ancho/alto) al contenedor,
  // para que el recuadro de la cámara se ajuste y siempre ocupe todo el ancho,
  // tanto en SD (~16:9) como en HD/Main Stream (4:3), sin recortar la imagen.
  const onAspectRatioRef = useRef(onAspectRatio);
  useEffect(() => {
    onAspectRatioRef.current = onAspectRatio;
  }, [onAspectRatio]);

  const onStreamReadyRef = useRef(onStreamReady);
  useEffect(() => {
    onStreamReadyRef.current = onStreamReady;
  }, [onStreamReady]);

  const onBandwidthRef = useRef(onBandwidth);
  useEffect(() => {
    onBandwidthRef.current = onBandwidth;
  }, [onBandwidth]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const report = () => {
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        onAspectRatioRef.current?.(video.videoWidth / video.videoHeight);
      }
    };
    // 'resize' del <video> se dispara cuando cambian las dimensiones intrínsecas
    // (p. ej. al alternar SD<->HD), así que también actualiza la relación.
    video.addEventListener('loadedmetadata', report);
    video.addEventListener('resize', report);
    report();
    return () => {
      video.removeEventListener('loadedmetadata', report);
      video.removeEventListener('resize', report);
    };
  }, []);

  // Aplicar/quitar el mute del audio sin reconectar el stream.
  // Solo se desmutea por gesto del usuario (icono de parlante), respetando la
  // política de autoplay de los navegadores.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !audioEnabled;
    if (audioEnabled) {
      video.play().catch(() => {
        /* el navegador puede rechazar si no hay gesto; se ignora */
      });
    }
  }, [audioEnabled]);

  // Variante A: ante cualquier cambio de visibilidad, re-disparar el efecto de
  // streaming. Su limpieza cierra la conexión al ocultarse y se reconecta al
  // volver. Así una cámara solo descarga video mientras la app está visible.
  useEffect(() => {
    const onVisibilityChange = () => {
      setReconnectNonce((n) => n + 1);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  // Medición periódica del ancho de banda entrante de esta conexión WebRTC (kbps).
  useEffect(() => {
    statsPrevRef.current = null;
    setBandwidthKbps(null);
    setBandwidthPending(false);
    onBandwidthRef.current?.(cameraId, null);

    const interval = setInterval(async () => {
      const pc = pcRef.current;
      if (
        !pc ||
        (pc.iceConnectionState !== 'connected' && pc.iceConnectionState !== 'completed')
      ) {
        return;
      }

      try {
        const stats = await pc.getStats();
        const totalBytes = sumInboundBytes(stats);
        const now = Date.now();
        const prev = statsPrevRef.current;

        if (prev !== null && totalBytes >= prev.bytes) {
          const deltaSec = (now - prev.time) / 1000;
          if (deltaSec > 0.2) {
            const kbps = (totalBytes - prev.bytes) * 8 / 1000 / deltaSec;
            setBandwidthKbps(kbps);
            setBandwidthPending(false);
            onBandwidthRef.current?.(cameraId, kbps);
          }
        } else if (prev === null && totalBytes > 0) {
          setBandwidthPending(true);
        }

        statsPrevRef.current = { bytes: totalBytes, time: now };
      } catch {
        /* getStats puede fallar transitoriamente al cerrar la conexión */
      }
    }, 1000);

    return () => {
      clearInterval(interval);
      statsPrevRef.current = null;
      setBandwidthKbps(null);
      setBandwidthPending(false);
      onBandwidthRef.current?.(cameraId, null);
    };
  }, [cameraId, quality, reconnectNonce]);

  useEffect(() => {
    // Capturar el elemento de video al inicio del efecto para usarlo en la
    // limpieza (el ref es estable durante la vida del componente).
    const videoEl = videoRef.current;
    let mounted = true;
    let whepResourceUrl: string | null = null;
    const abortController = new AbortController();
    const { signal } = abortController;
    const peerConnections = new Set<RTCPeerConnection>();

    const isActive = (): boolean => mounted && !signal.aborted;

    const registerPeerConnection = (pc: RTCPeerConnection): boolean => {
      peerConnections.add(pc);
      pcRef.current = pc;
      if (!isActive()) {
        closePeerConnection(pc);
        peerConnections.delete(pc);
        if (pcRef.current === pc) {
          pcRef.current = null;
        }
        return false;
      }
      return true;
    };

    const dropPeerConnection = (pc: RTCPeerConnection): void => {
      closePeerConnection(pc);
      peerConnections.delete(pc);
      if (pcRef.current === pc) {
        pcRef.current = null;
      }
    };

    const sleep = (ms: number): Promise<void> =>
      new Promise((resolve, reject) => {
        if (signal.aborted) {
          reject(new Error('aborted'));
          return;
        }
        const timer = setTimeout(resolve, ms);
        signal.addEventListener(
          'abort',
          () => {
            clearTimeout(timer);
            reject(new Error('aborted'));
          },
          { once: true }
        );
      });

    const guardActive = (pc?: RTCPeerConnection): void => {
      if (!isActive()) {
        if (pc) {
          dropPeerConnection(pc);
        }
        throw new Error('session-inactive');
      }
    };

    let retryCount = 0;
    const maxRetries = isSafariOrWebKit() ? 2 : 0; // Solo reintentar en Safari/WebKit
    let whepRetryCount = 0; // Contador específico para reintentos de errores WHEP 404
    const maxWhepRetries = 3; // Máximo 3 reintentos para errores 404 de MediaMTX
    let streamGeneration = 0;
    let firstFrameWatchdog: ReturnType<typeof setTimeout> | null = null;
    const FIRST_FRAME_TIMEOUT_MS = 8000;
    const MAX_EMPTY_FRAME_RETRIES = 3;

    const clearFirstFrameWatchdog = () => {
      if (firstFrameWatchdog) {
        clearTimeout(firstFrameWatchdog);
        firstFrameWatchdog = null;
      }
    };

    const startStream = async (
      resetWhepRetry: boolean = false,
      options?: { skipDelay?: boolean }
    ): Promise<void> => {
      const generation = ++streamGeneration;
      const isCurrent = (): boolean => isActive() && generation === streamGeneration;
      clearFirstFrameWatchdog();

      // Resetear contador de reintentos WHEP si se solicita (nueva conexión inicial)
      if (resetWhepRetry) {
        whepRetryCount = 0;
      }
      
      // ========== LOG: Inicio de conexión ==========
      console.log(`[WebRTC-${cameraId}] ========== INICIO DE CONEXIÓN ==========`);
      // console.log(`[WebRTC-${connectionId}] User Agent: ${navigator.userAgent}`);
      // console.log(`[WebRTC-${connectionId}] Navegador: ${isSafariOrWebKit() ? 'Safari/WebKit' : 'Otro'}`);
      
      try {
        guardActive();
        setLoading(true);
        setError(null);

        // Variante A: no abrir (ni mantener) tráfico de red si la app no está
        // visible. La reconexión ocurre al volver, vía el efecto de visibilidad.
        // Timbre (connectWhileHidden): el tap de la notificación a veces pinta hidden.
        if (typeof document !== 'undefined' && document.hidden && !connectWhileHidden) {
          return;
        }

        if (!options?.skipDelay && connectDelayMs > 0) {
          await sleep(connectDelayMs);
          guardActive();
        }

        guardActive();

        if (!videoRef.current) {
          throw new Error('Video element not available');
        }

        const video = videoRef.current;

        // En HD se usa el Main Stream (path camera_<id>_hd, bajo demanda en MediaMTX);
        // por defecto (SD) se usa el Sub Stream 1.
        const pathName = quality === 'hd' ? `camera_${cameraId}_hd` : `camera_${cameraId}`;
        const whepUrl = `/webrtc/${pathName}/whep`;
        const authToken = getToken();
        if (!authToken) {
          throw new Error('Sesión no autenticada');
        }
        
        // ========== LOG: URL WHEP ==========
        // console.log(`[WebRTC-${connectionId}] URL WHEP: ${whepUrl}`);
        // console.log(`[WebRTC-${connectionId}] URL completa: ${window.location.origin}${whepUrl}`);

        // Configuración específica para Safari/WebKit (iOS y macOS Safari)
        // No usamos 'max-bundle' porque Safari requiere grupos BUNDLE en el SDP
        // que no siempre se generan automáticamente
        const safariConfig = isSafariOrWebKit() ? {
          iceTransportPolicy: 'all' as RTCIceTransportPolicy,
          bundlePolicy: 'balanced' as RTCBundlePolicy, // Cambiado de 'max-bundle' a 'balanced'
        } : {};

        // ========== LOG: Configuración STUN ==========
        // console.log(`[WebRTC-${connectionId}] Servidores STUN configurados:`);
        // console.log(`[WebRTC-${connectionId}]   - stun:stun.l.google.com:19302`);
        // console.log(`[WebRTC-${connectionId}]   - stun:stun1.l.google.com:19302`);
        if (isSafariOrWebKit()) {
          // console.log(`[WebRTC-${connectionId}] Configuración Safari: iceTransportPolicy=all, bundlePolicy=balanced`);
        }

        // Crear RTCPeerConnection
        const pc = new RTCPeerConnection({
          iceServers: [
            { urls: 'stun:stun.l.google.com:19302' },
            { urls: 'stun:stun1.l.google.com:19302' },
          ],
          ...safariConfig,
        });

        if (!registerPeerConnection(pc)) {
          return;
        }

        // Manejar tracks de video/audio
        let currentStream: MediaStream | null = null;
        let gotFirstFrame = false;

        const markFirstFrameReady = () => {
          if (!isCurrent() || gotFirstFrame) {
            return;
          }
          gotFirstFrame = true;
          emptyFrameRetriesRef.current = 0;
          clearFirstFrameWatchdog();
          setLoading(false);
          setError(null);
        };

        const attachFirstFrameListeners = (videoEl: HTMLVideoElement) => {
          const considerReady = () => {
            if (videoEl.videoWidth > 0 && videoEl.videoHeight > 0) {
              markFirstFrameReady();
            }
          };
          videoEl.addEventListener('loadeddata', considerReady);
          videoEl.addEventListener('playing', considerReady);
          videoEl.addEventListener('resize', considerReady);
          const rvfc = (
            videoEl as HTMLVideoElement & {
              requestVideoFrameCallback?: (cb: () => void) => number;
            }
          ).requestVideoFrameCallback;
          if (typeof rvfc === 'function') {
            rvfc.call(videoEl, () => markFirstFrameReady());
          }
          considerReady();
        };

        const retryEmptyStream = async () => {
          if (!isCurrent() || gotFirstFrame) {
            return;
          }
          if (emptyFrameRetriesRef.current >= MAX_EMPTY_FRAME_RETRIES) {
            setError('Sin imagen del stream');
            setLoading(false);
            onError?.('Sin imagen del stream');
            return;
          }
          emptyFrameRetriesRef.current += 1;
          void tearDownWhepSession(whepResourceUrl, authToken);
          whepResourceUrl = null;
          dropPeerConnection(pc);
          try {
            await sleep(400);
            guardActive();
            return startStream(false, { skipDelay: true });
          } catch {
            return;
          }
        };

        const armFirstFrameWatchdog = () => {
          if (gotFirstFrame) {
            return;
          }
          clearFirstFrameWatchdog();
          firstFrameWatchdog = setTimeout(() => {
            firstFrameWatchdog = null;
            void retryEmptyStream();
          }, FIRST_FRAME_TIMEOUT_MS);
        };

        pc.ontrack = (event) => {
          if (!isCurrent()) {
            try {
              event.track.stop();
            } catch {
              /* no-op */
            }
            return;
          }
          // El audio viaja en el mismo MediaStream que el video (event.streams[0]),
          // así que asignar el stream cuando llega cualquier pista cubre ambos casos.
          if ((event.track.kind === 'video' || event.track.kind === 'audio') && videoRef.current) {
            const videoElTrack = videoRef.current;
            currentStream = event.streams[0] ?? new MediaStream([event.track]);
            videoElTrack.srcObject = currentStream;
            videoElTrack.muted = !audioEnabled;
            videoElTrack.playsInline = true;
            videoElTrack.setAttribute('playsinline', '');
            videoElTrack.setAttribute('webkit-playsinline', '');
            onStreamReadyRef.current?.(currentStream);
            // ICE / ontrack no implican frames. El spinner se oculta al primer cuadro real.
            if (event.track.kind === 'video') {
              attachFirstFrameListeners(videoElTrack);
              armFirstFrameWatchdog();
            }
            videoElTrack.play().catch(() => {
              /* el navegador puede rechazar si no hay gesto; se ignora */
            });
          }
        };

        // Manejar cambios de estado ICE
        let previousIceState = '';
        const DISCONNECTED_GRACE_MS = 6000; // "disconnected" puede recuperarse; esperar antes de mostrar error

        pc.oniceconnectionstatechange = () => {
          const state = pc.iceConnectionState;

          if (state !== previousIceState) {
            previousIceState = state;
          }

          if (state === 'connected' || state === 'completed') {
            if (disconnectedTimeoutRef.current) {
              clearTimeout(disconnectedTimeoutRef.current);
              disconnectedTimeoutRef.current = null;
            }
            if (isCurrent() && gotFirstFrame) {
              setError(null);
            } else if (isCurrent()) {
              // Conectó ICE pero aún no hay imagen: seguir en spinner y vigilar frames.
              armFirstFrameWatchdog();
            }
          } else if (state === 'failed') {
            if (disconnectedTimeoutRef.current) {
              clearTimeout(disconnectedTimeoutRef.current);
              disconnectedTimeoutRef.current = null;
            }
            if (mounted) {
              setError('Conexión WebRTC failed');
              setLoading(false);
              onError?.('Conexión WebRTC failed');
            }
          } else if (state === 'disconnected') {
            // No mostrar error de inmediato: "disconnected" puede ser temporal (3-7s de recuperación)
            if (disconnectedTimeoutRef.current) return;
            disconnectedTimeoutRef.current = setTimeout(() => {
              disconnectedTimeoutRef.current = null;
              if (!mounted || !pcRef.current) return;
              const current = pc.iceConnectionState;
              if (current === 'disconnected' || current === 'failed') {
                setError('Conexión WebRTC desconectada');
                setLoading(false);
                onError?.('Conexión WebRTC desconectada');
              }
            }, DISCONNECTED_GRACE_MS);
          }
        };

        // Variable para rastrear recolección ICE
        let iceGatheringComplete = false;
        let hasHostCandidate = false;
        let clientIceCandidates: Array<{candidate: string, type: string, ip: string, port: string}> = [];

        const markHostCandidate = (candidate: RTCIceCandidate) => {
          if (candidate.type === 'host') {
            hasHostCandidate = true;
            return;
          }
          if (typeof candidate.candidate === 'string' && /\btyp host\b/.test(candidate.candidate)) {
            hasHostCandidate = true;
          }
        };

        // Sin trickle ICE: el POST lleva los candidatos ya juntados. No esperamos
        // STUN (srflx): con un candidato host alcanza para LAN y el caso típico.
        pc.onicecandidate = (event) => {
          if (event.candidate) {
            const candidateStr = event.candidate.candidate;
            
            // Extraer información del candidato
            const candidateMatch = candidateStr.match(/candidate:(\d+) (\d+) (\w+) (\d+) (\S+) (\d+) typ (\w+)/);
            if (candidateMatch) {
              const [, , , , , ip, port, type] = candidateMatch;
              clientIceCandidates.push({ candidate: candidateStr, type, ip, port });
            }
            markHostCandidate(event.candidate);
          } else {
            // null candidate significa que la recolección de ICE ha terminado
            iceGatheringComplete = true;
            // console.log(`[WebRTC-${connectionId}] ========== RECOLECCIÓN ICE COMPLETA ==========`);
            // console.log(`[WebRTC-${connectionId}] Total de candidatos generados: ${clientIceCandidates.length}`);
            clientIceCandidates.forEach((cand, idx) => {
              // console.log(`[WebRTC-${connectionId}]   Candidato ${idx + 1}: ${cand.type} - ${cand.ip}:${cand.port}`);
            });
          }
        };

        pc.onicegatheringstatechange = () => {
          if (pc.iceGatheringState === 'complete') {
            iceGatheringComplete = true;
          }
        };

        // En Safari/WebKit, agregar explícitamente un transceiver para recibir video
        // Esto asegura que Safari genere el SDP completo con la sección de media
        if (isSafariOrWebKit()) {
          try {
            pc.addTransceiver('video', { direction: 'recvonly' });
            // También recibir audio (G.711) de la cámara en Safari/WebKit
            pc.addTransceiver('audio', { direction: 'recvonly' });
            console.log('[VideoStreamViewer] Transceivers de video y audio agregados para Safari');
          } catch (err) {
            console.warn('[VideoStreamViewer] Error agregando transceiver:', err);
          }
        }

        // Crear oferta SDP con configuración específica para Safari/WebKit
        // Se solicita audio para poder escuchar el micrófono de la cámara (G.711A).
        const offerOptions: RTCOfferOptions = {
          offerToReceiveVideo: true,
          offerToReceiveAudio: true,
        };

        // En Safari/WebKit, usar iceRestart puede ayudar con problemas de conexión
        if (isSafariOrWebKit() && retryCount > 0) {
          offerOptions.iceRestart = true;
        }

        const offer = await pc.createOffer(offerOptions);
        guardActive(pc);

        if (!offer.sdp) {
          throw new Error('SDP offer is undefined');
        }

        let offerSdp = offer.sdp;
        
        // ========== LOG: SDP Offer ==========
        // console.log(`[WebRTC-${connectionId}] ========== SDP OFFER GENERADO ==========`);
        // console.log(`[WebRTC-${connectionId}] Longitud SDP: ${offerSdp.length} caracteres`);
        // console.log(`[WebRTC-${connectionId}] Tiene ice-ufrag: ${offerSdp.includes('a=ice-ufrag:')}`);
        // console.log(`[WebRTC-${connectionId}] Tiene ice-pwd: ${offerSdp.includes('a=ice-pwd:')}`);
        // console.log(`[WebRTC-${connectionId}] Tiene fingerprint: ${offerSdp.includes('a=fingerprint:')}`);
        console.log(`[WebRTC] Preview SDP (primeras 20 líneas):\n${offerSdp.split('\r\n').slice(0, 20).join('\n')}`);

        // En Safari/WebKit, esperar un poco antes de establecer la descripción local
        if (isSafariOrWebKit()) {
          await sleep(100);
          guardActive(pc);
        }

        await pc.setLocalDescription({
          type: 'offer',
          sdp: offerSdp,
        });
        guardActive(pc);

        // POST en cuanto hay candidato host (o terminó el gathering). No esperar STUN.
        const maxWaitTime = isSafariOrWebKit() ? 400 : 250;
        const startTime = Date.now();
        while (!iceGatheringComplete && !hasHostCandidate && (Date.now() - startTime) < maxWaitTime) {
          guardActive(pc);
          await sleep(20);
        }
        await sleep(20);
        guardActive(pc);

        // Obtener SDP con todos los candidatos incluidos
        if (pc.localDescription && pc.localDescription.sdp) {
          offerSdp = pc.localDescription.sdp;
        }

        // En Safari/WebKit, normalizar SDP (fingerprint, ice-ufrag, ice-pwd)
        if (isSafariOrWebKit()) {
          let updatedSdp = offerSdp;
          
          // Si el SDP sigue siendo muy corto (menos de 200 caracteres), puede que Safari no lo haya completado
          if (updatedSdp.length < 200) {
            console.warn('[VideoStreamViewer] SDP muy corto, esperando más tiempo...');
            await sleep(1000);
            guardActive(pc);
            
            if (pc.localDescription && pc.localDescription.sdp) {
              const retrySdp = pc.localDescription.sdp;
              if (retrySdp.length > updatedSdp.length) {
                updatedSdp = retrySdp;
                console.log('[VideoStreamViewer] SDP actualizado después de espera adicional, longitud:', updatedSdp.length);
              }
            }
          }
          
          // Guardar el SDP original antes de normalizar para preservar el fingerprint
          const originalSdp = updatedSdp;
          const hasFingerprintBefore = originalSdp.includes('a=fingerprint:');
          
          console.log('[VideoStreamViewer] Antes de normalizar - tiene fingerprint:', hasFingerprintBefore, 'longitud:', originalSdp.length);
          
          // Normalizar la oferta SDP para asegurar que tenga atributos ICE
          // Esto es CRÍTICO: MediaMTX requiere ice-ufrag e ice-pwd en la oferta
          updatedSdp = normalizeOfferSDPForSafari(updatedSdp);
          
          // Si perdimos el fingerprint durante la normalización, restaurarlo del original
          if (hasFingerprintBefore && !updatedSdp.includes('a=fingerprint:')) {
            console.warn('[VideoStreamViewer] Fingerprint perdido durante normalización, restaurándolo...');
            // Extraer el fingerprint del SDP original
            const fingerprintMatch = originalSdp.match(/a=fingerprint:([^\r\n]+)/);
            if (fingerprintMatch) {
              const fingerprintLine = `a=fingerprint:${fingerprintMatch[1]}`;
              // Insertar el fingerprint después de los atributos ICE
              const lines = updatedSdp.split('\r\n');
              const icePwdIndex = lines.findIndex(line => line.startsWith('a=ice-pwd:'));
              if (icePwdIndex >= 0) {
                lines.splice(icePwdIndex + 1, 0, fingerprintLine);
                updatedSdp = lines.join('\r\n');
                console.log('[VideoStreamViewer] Fingerprint restaurado');
              } else {
                // Si no hay ice-pwd, agregar al final de los atributos
                // Buscar el último atributo 'a=' desde el final del array
                let lastAttrIndex = -1;
                for (let i = lines.length - 1; i >= 0; i--) {
                  if (lines[i].startsWith('a=')) {
                    lastAttrIndex = i;
                    break;
                  }
                }
                if (lastAttrIndex >= 0) {
                  lines.splice(lastAttrIndex + 1, 0, fingerprintLine);
                  updatedSdp = lines.join('\r\n');
                  console.log('[VideoStreamViewer] Fingerprint restaurado al final');
                }
              }
            }
          } else if (!hasFingerprintBefore) {
            // Si nunca tuvo fingerprint, intentar agregar uno dummy (esto puede no funcionar, pero lo intentamos)
            console.warn('[VideoStreamViewer] SDP nunca tuvo fingerprint, Safari puede no estar generándolo');
            // No podemos generar un fingerprint válido sin un certificado DTLS, así que esto es un problema
          }
          
          offerSdp = updatedSdp;
          
          // Verificar que tenga los atributos necesarios
          if (!offerSdp.includes('a=ice-ufrag:') || !offerSdp.includes('a=ice-pwd:')) {
            console.error('[VideoStreamViewer] ERROR: SDP normalizado aún no tiene atributos ICE');
            throw new Error('No se pudieron agregar atributos ICE al SDP para Safari');
          }
          
          // Verificar que tenga fingerprint después de normalizar
          if (!offerSdp.includes('a=fingerprint:')) {
            console.error('[VideoStreamViewer] ERROR: SDP no tiene fingerprint después de normalizar');
            console.error('[VideoStreamViewer] SDP completo:', offerSdp);
            // En lugar de lanzar error, intentar enviar sin fingerprint (puede que MediaMTX lo acepte)
            console.warn('[VideoStreamViewer] Intentando enviar SDP sin fingerprint (MediaMTX puede rechazarlo)');
          }
        }

        // En Safari/WebKit, agregar logging para diagnóstico
        if (isSafariOrWebKit()) {
          const hasFingerprint = offerSdp.includes('a=fingerprint:');
          // console.log('[VideoStreamViewer] Enviando oferta SDP a MediaMTX:', {
            // // // hasIceUfrag,
            // // // hasIcePwd,
            // // // hasFingerprint,
            // // // sdpLength: offerSdp.length,
            // // // firstLines: offerSdp.split('\r\n').slice(0, 15).join('\n')
          // // // });
          
          // Si falta el fingerprint, es un error crítico
          if (!hasFingerprint) {
            console.error('[VideoStreamViewer] ERROR CRÍTICO: SDP no tiene fingerprint');
          }
        }

        // Enviar oferta SDP a MediaMTX usando WHEP (POST)
        // console.log(`[WebRTC-${connectionId}] ========== ENVIANDO SDP OFFER (POST) ==========`);
        // console.log(`[WebRTC-${connectionId}] URL: ${whepUrl}`);
        // console.log(`[WebRTC-${connectionId}] Método: POST`);
        // console.log(`[WebRTC-${connectionId}] Content-Type: application/sdp`);
        let response = await fetch(whepUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/sdp',
            Authorization: `Bearer ${authToken}`,
          },
          body: offerSdp,
          signal,
        });
        guardActive(pc);
        
        console.log(`[WebRTC] Status: ${response.status} ${response.statusText}`);

        // Manejar errores 404 con retry y backoff exponencial
        // Esto soluciona race conditions cuando MediaMTX aún no ha recargado después de editar una cámara
        if (!response.ok && response.status === 404 && whepRetryCount < maxWhepRetries) {
          const errorText = await response.text();
          const isPathNotFound = errorText.includes('no stream is available') || 
                                 errorText.includes('not found') ||
                                 errorText.includes('404');
          
          if (isPathNotFound) {
            whepRetryCount++;
            // Backoff exponencial: 500ms, 1000ms, 1500ms
            const delay = 500 * whepRetryCount;
            
            // Cerrar conexión anterior antes de reintentar
            void tearDownWhepSession(whepResourceUrl, authToken);
            whepResourceUrl = null;
            dropPeerConnection(pc);
            
            // Esperar antes de reintentar
            await sleep(delay);
            guardActive();
            
            // Reintentar creando una nueva conexión (mantener contador de reintentos)
            return startStream(false);
          }
        }

        if (!response.ok) {
          const errorText = await response.text();
          // console.error(`[WebRTC-${connectionId}] ========== ERROR DE MEDIAMTX ==========`);
          // console.error(`[WebRTC-${connectionId}] Status: ${response.status} ${response.statusText}`);
          // console.error(`[WebRTC-${connectionId}] Error: ${errorText}`);
          // console.error(`[WebRTC-${connectionId}] Es Safari/WebKit: ${isSafariOrWebKit()}`);
          // console.error(`[WebRTC-${connectionId}] Preview SDP offer: ${offerSdp.substring(0, 500)}`);
          throw new Error(`MediaMTX WHEP error: ${response.status} - ${errorText}`);
        }

        // Guardar URL de sesión WHEP para DELETE explícito al cerrar (libera al instante en MediaMTX).
        whepResourceUrl = resolveWhepResourceUrl(response.headers.get('Location'), whepUrl);

        let answerSdp = await response.text();
        guardActive(pc);

        if (!answerSdp || answerSdp.trim() === '') {
          // console.error(`[WebRTC-${connectionId}] ERROR: MediaMTX no devolvió SDP answer`);
          throw new Error('MediaMTX no devolvió SDP answer');
        }
        
        // ========== LOG: SDP Answer ==========
        // console.log(`[WebRTC-${connectionId}] ========== SDP ANSWER RECIBIDO ==========`);
        // console.log(`[WebRTC-${connectionId}] Longitud SDP: ${answerSdp.length} caracteres`);
        // console.log(`[WebRTC-${connectionId}] Tiene ice-ufrag: ${answerSdp.includes('a=ice-ufrag:')}`);
        // console.log(`[WebRTC-${connectionId}] Tiene ice-pwd: ${answerSdp.includes('a=ice-pwd:')}`);
        // console.log(`[WebRTC-${connectionId}] Tiene fingerprint: ${answerSdp.includes('a=fingerprint:')}`);
        
        // Extraer candidatos ICE del servidor del SDP answer
        const serverCandidates: Array<{candidate: string, type: string, ip: string, port: string}> = [];
        const answerLines = answerSdp.split('\r\n');
        answerLines.forEach(line => {
          if (line.startsWith('a=candidate:')) {
            const candidateMatch = line.match(/a=candidate:(\d+) (\d+) (\w+) (\d+) (\S+) (\d+) typ (\w+)/);
            if (candidateMatch) {
              const [, , , , , ip, port, type] = candidateMatch;
              serverCandidates.push({ candidate: line, type, ip, port });
              // console.log(`[WebRTC-${connectionId}] ========== CANDIDATO ICE SERVIDOR ==========`);
              // console.log(`[WebRTC-${connectionId}]   IP: ${ip}`);
              // console.log(`[WebRTC-${connectionId}]   Puerto: ${port}`);
              // console.log(`[WebRTC-${connectionId}]   Tipo: ${type} (host=local, srflx=pública, relay=TURN)`);
              // console.log(`[WebRTC-${connectionId}]   Transport: ${transport}`);
              // console.log(`[WebRTC-${connectionId}]   Candidato completo: ${line}`);
            }
          }
        });
        
        if (serverCandidates.length === 0) {
          // console.warn(`[WebRTC-${connectionId}] ADVERTENCIA: No se encontraron candidatos ICE del servidor en el SDP answer`);
        } else {
          // console.log(`[WebRTC-${connectionId}] Total candidatos del servidor: ${serverCandidates.length}`);
        }
        
        console.log(`[WebRTC] Preview SDP answer (primeras 20 líneas):\n${answerSdp.split('\r\n').slice(0, 20).join('\n')}`);

        // Normalizar SDP para Safari/WebKit antes de establecerlo
        if (isSafariOrWebKit()) {
          answerSdp = normalizeSDPForSafari(answerSdp);
          
          // Validar que el SDP tenga los atributos ICE necesarios
          if (!answerSdp.includes('a=ice-ufrag:') || !answerSdp.includes('a=ice-pwd:')) {
            console.warn('[VideoStreamViewer] SDP answer no contiene atributos ICE requeridos para Safari/WebKit');
            // Intentar agregar atributos ICE básicos si faltan completamente
            const lines = answerSdp.split('\r\n');
            const mLineIndex = lines.findIndex(line => line.startsWith('m='));
            if (mLineIndex >= 0) {
              // Buscar si hay atributos ICE en alguna parte
              const hasIceUfrag = lines.some(line => line.startsWith('a=ice-ufrag:'));
              const hasIcePwd = lines.some(line => line.startsWith('a=ice-pwd:'));
              
              if (!hasIceUfrag || !hasIcePwd) {
                // Generar valores ICE básicos (esto es un fallback, idealmente MediaMTX debería proporcionarlos)
                const iceUfrag = Math.random().toString(36).substring(2, 15);
                const icePwd = Math.random().toString(36).substring(2, 15);
                
                // Insertar después de la línea m=
                lines.splice(mLineIndex + 1, 0, `a=ice-ufrag:${iceUfrag}`, `a=ice-pwd:${icePwd}`);
                answerSdp = lines.join('\r\n');
              }
            }
          }
        }

        // Establecer respuesta SDP remota
        // console.log(`[WebRTC-${connectionId}] ========== ESTABLECIENDO SDP REMOTO ==========`);
        try {
          await pc.setRemoteDescription({
            type: 'answer',
            sdp: answerSdp,
          });
          guardActive(pc);
          armFirstFrameWatchdog();
          // console.log(`[WebRTC-${connectionId}] ✅ SDP remoto establecido correctamente`);
        } catch (sdpError: any) {
          // Si falla en Safari/WebKit y es el error de ice-ufrag, intentar retry
          if (isSafariOrWebKit() && retryCount < maxRetries && 
              (sdpError.message?.includes('ice-ufrag') || sdpError.message?.includes('SetRemoteDescription'))) {
            retryCount++;
            
            // Cerrar conexión anterior
            void tearDownWhepSession(whepResourceUrl, authToken);
            whepResourceUrl = null;
            dropPeerConnection(pc);
            
            // Esperar un poco antes de reintentar
            await sleep(1000);
            guardActive();
            
            // Reintentar (mantener contador de reintentos)
            return startStream(false);
          }
          throw sdpError;
        }

        // Configurar video
        video.playsInline = true;
        video.muted = !audioEnabled;
        video.autoplay = true;

      } catch (err: any) {
        if (
          err?.message === 'session-inactive' ||
          err?.message === 'aborted' ||
          err?.name === 'AbortError'
        ) {
          return;
        }
        
        if (mounted) {
          let errorMsg = err.response?.data?.error || err.message || 'Error desconocido';
          
          // Mensaje más amigable para el error específico de Safari/WebKit
          if (isSafariOrWebKit() && (errorMsg.includes('ice-ufrag') || errorMsg.includes('SetRemoteDescription'))) {
            errorMsg = 'Error de conexión WebRTC en Safari. Por favor, intenta cerrar y reabrir la aplicación o usar Chrome.';
          }
          
          setError(errorMsg);
          setLoading(false);
          onError?.(errorMsg);
        }
      }
    };

    startStream(true); // Iniciar con reset del contador de reintentos WHEP

    return () => {
      mounted = false;
      streamGeneration += 1;
      abortController.abort();
      clearFirstFrameWatchdog();
      if (disconnectedTimeoutRef.current) {
        clearTimeout(disconnectedTimeoutRef.current);
        disconnectedTimeoutRef.current = null;
      }

      const resourceUrl = whepResourceUrl;
      whepResourceUrl = null;
      const token = getToken();
      const pcsToClose = Array.from(peerConnections);
      peerConnections.clear();
      pcRef.current = null;

      // DELETE WHEP antes de cerrar el RTCPeerConnection: MediaMTX libera la sesión
      // al instante; sin DELETE válido el servidor puede seguir enviando RTP hasta
      // detectar el cierre ICE (timeout interno ~20 s, ver mediamtx #4477).
      onStreamReadyRef.current?.(null);

      void (async () => {
        await tearDownWhepSession(resourceUrl, token);
        pcsToClose.forEach((pc) => closePeerConnection(pc));
        if (videoEl) {
          const stream = videoEl.srcObject as MediaStream | null;
          if (stream) {
            try {
              stream.getTracks().forEach((track) => track.stop());
            } catch {
              /* no-op */
            }
          }
          videoEl.srcObject = null;
          videoEl.removeAttribute('src');
        }
      })();
    };
    // quality en deps: cambiar SD<->HD reconecta al path correspondiente (cerrando
    // la conexión anterior, así nunca quedan SD y HD activas a la vez).
    // reconnectNonce en deps: reconectar al volver a estar visible la app.
    // audioEnabled NO va en deps (se maneja en su propio efecto) para no reconectar al mutear.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraId, quality, onError, reconnectNonce, connectDelayMs, connectWhileHidden]);

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        position: 'relative',
        backgroundColor: '#000',
        margin: 0,
        padding: 0,
        boxSizing: 'border-box',
        overflow: 'hidden',
        display: 'block',
        // CRÍTICO: Cuando NO hay zoom, NO bloquear eventos de touch para scroll
        pointerEvents: hasZoom ? 'auto' : 'none',
        touchAction: hasZoom ? 'none' : 'pan-y', // ✅ Permitir scroll cuando no hay zoom
      }}
    >
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: '42%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <CircularProgress size={24} sx={{ color: '#2391FF' }} />
          <Typography variant="body2" sx={{ color: '#fff' }}>
            {cameraName}
          </Typography>
        </Box>
      )}

      {error && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10,
            textAlign: 'center',
            padding: 2,
            backgroundColor: 'rgba(0,0,0,0.8)',
            borderRadius: 1,
            maxWidth: '90%',
          }}
        >
          <Typography variant="body2" sx={{ color: '#ff4444', mb: 1 }}>
            Error: {error}
          </Typography>
          <Typography variant="caption" sx={{ color: '#888' }}>
            Cámara: {cameraName}
          </Typography>
        </Box>
      )}

      {showBandwidth && !error && !loading && (bandwidthKbps !== null || bandwidthPending) && (
        <Box
          sx={{
            position: 'absolute',
            top: 6,
            left: 6,
            zIndex: 11,
            px: 0.6,
            py: 0.15,
            borderRadius: 0.5,
            backgroundColor: 'rgba(0, 0, 0, 0.42)',
            color: 'rgba(255, 255, 255, 0.72)',
            fontSize: '0.58rem',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            lineHeight: 1.25,
            letterSpacing: '0.02em',
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        >
          {bandwidthKbps !== null ? formatBandwidthKbps(bandwidthKbps) : '…'}
        </Box>
      )}

      <video
        ref={videoRef}
        autoPlay
        muted={!audioEnabled}
        playsInline
        disablePictureInPicture
        controls={false}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'contain', // Siempre usar contain para mantener relación de aspecto y no recortar
          objectPosition,
          display: error ? 'none' : 'block',
          backgroundColor: '#000',
          margin: 0,
          padding: 0,
          border: 'none',
          outline: 'none',
          boxSizing: 'border-box',
          // CRÍTICO: Cuando NO hay zoom, hacer que el video no bloquee eventos de touch para scroll
          pointerEvents: hasZoom ? 'auto' : 'none',
          touchAction: hasZoom ? 'none' : 'pan-y',
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none',
          userSelect: 'none',
        }}
      />
    </Box>
  );
};

export default VideoStreamViewer;
